import { FetchUtil } from "../util/FetchUtil";
import { ITopic } from "../models/ITopic";
import { StringUtil } from "../util/StringUtil";
import { ITopicListServiceEndpoint } from "../models/TopicList/ITopicListServiceEndpoint";
import { ICreditType } from "../models/ICreditType";

interface IRawTopic {
  readonly TopicId: string;
  readonly TopicName: string;
}

interface IRawCreditType {
  readonly CreditTypeId: string;
  readonly CreditTypeName: string;
}

interface ITopicAndCreditTypeResponse {
  topics: IRawTopic[];
  credittypes: IRawCreditType[];
}

export class TopicListService {
  private static readonly _cache = new Map<string, ITopic[]>();
  private static readonly _topicsAndCreditTypesCache = new Map<string, {topics: ITopic[], creditTypes: ICreditType[]}>();

  constructor(private readonly _endpoint: ITopicListServiceEndpoint) {}

  private static mapTopics(raw: IRawTopic): ITopic {
    return {
      topicId: raw.TopicId,
      topicName: raw.TopicName,
    };
  }

  private static mapCreditTypes(raw: IRawCreditType): ICreditType {
    return {
      creditTypeId: raw.CreditTypeId,
      creditTypeName: raw.CreditTypeName,
    };
  }

  public async getTopicsBySpecialty(specialtyId: string): Promise<ITopic[]> {
    const cacheHit = TopicListService._cache.get(specialtyId);
    if (cacheHit) {
      return cacheHit;
    }

    if (!this._endpoint.topics) {
      throw new Error("No endpoint available.");
    }

    const url = StringUtil.supplant(this._endpoint.topics.url, {
      encodedSpecialtyId: encodeURIComponent(specialtyId),
      specialtyId,
    });

    if (!url) {
      throw new Error("Unable to resolve endpoint.");
    }

    let raw;
    try {
      raw = await FetchUtil.sendMessage({
        method: this._endpoint.topics.method,
        url,
      });
    } catch {
      raw = undefined;
    }

    if (!Array.isArray(raw)) {
      throw new Error("Unexpected response from server");
    }

    const result = raw.map(TopicListService.mapTopics);

    TopicListService._cache.set(specialtyId, result);

    return result;
  }

  public async getTopicsAndCreditTypesBySpecialty(specialtyId: string): Promise<{topics: ITopic[], creditTypes: ICreditType[]}> {
    const cacheHit = TopicListService._topicsAndCreditTypesCache.get(specialtyId);
    if (cacheHit) {
      return cacheHit;
    }

    if (!this._endpoint.topicsAndCreditTypes) {
      throw new Error("No endpoint available.");
    }

    const url = StringUtil.supplant(this._endpoint.topicsAndCreditTypes.url, {
      encodedSpecialtyId: encodeURIComponent(specialtyId),
      specialtyId,
    });

    if (!url) {
      throw new Error("Unable to resolve endpoint.");
    }

    let raw;
    try {
      raw = await FetchUtil.sendMessage({
        method: this._endpoint.topicsAndCreditTypes.method,
        url,
      });
    } catch {
      raw = undefined;
    }

    const response = raw as ITopicAndCreditTypeResponse;

    let result = {
      topics: response.topics.length > 0 ? response.topics.map(TopicListService.mapTopics) : [],
      creditTypes: response.credittypes.length > 0 && response.credittypes[0] != null ? response.credittypes?.map(TopicListService.mapCreditTypes) : []
    }
    TopicListService._topicsAndCreditTypesCache.set(specialtyId, result);

    return result;
  }
}
